import { useEffect, useState } from "react";
import { Settings } from "lucide-react"; // Engrenagem animada
import logo from "../images/logo.png";
import { API_URL } from "../config";

export default function AtualizacaoSistema({ usuarioId, onFinalizado }) {
  const [progresso, setProgresso] = useState(0);
  const [step, setStep] = useState(null);
  const [dadosVersao, setDadosVersao] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    async function iniciarAtualizacao() {
      try {
        const res = await fetch(`${API_URL}/verificar_atualizacao.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ usuario_id: usuarioId }),
        });

        const dados = await res.json();

        if (!dados.mostrar_novidades) {
          onFinalizado();
          return;
        }

        setDadosVersao(dados);
        setStep("carregando");

        const duration = 20000;
        const interval = 200;
        let elapsed = 0;

        const progressInterval = setInterval(() => {
          elapsed += interval;
          setProgresso(Math.min((elapsed / duration) * 100, 100));
        }, interval);

        setTimeout(() => {
          clearInterval(progressInterval);
          setStep("sucesso");

          setTimeout(() => {
            setStep("novidades");
          }, 3000);
        }, duration);
      } catch (error) {
        console.error("Erro ao buscar versão:", error);
        setErro(true);
        setTimeout(onFinalizado, 3000);
      }
    }

    iniciarAtualizacao();
  }, [usuarioId, onFinalizado]);

  useEffect(() => {
    const escHandler = (e) => {
      if (e.key === "Escape" && step === "novidades") {
        marcarAtualizacaoComoVista();
      }
    };
    document.addEventListener("keydown", escHandler);
    return () => document.removeEventListener("keydown", escHandler);
  }, [step]);

  const marcarAtualizacaoComoVista = async () => {
    try {
      await fetch(`${API_URL}/marcar_atualizacao.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario_id: usuarioId }),
      });
    } catch (error) {
      console.error("Erro ao marcar visualização da atualização:", error);
    }
    onFinalizado();
  };

  if (erro) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-black/80 text-red-500 text-xl font-semibold">
        Erro ao carregar atualizações. Redirecionando...
      </div>
    );
  }

  if (step === "carregando") {
    return (
      <div
        className="h-screen w-screen flex flex-col items-center justify-center text-white"
        style={{ backgroundColor: "#44749d" }}
      >
        {/* Logo */}
        <img src={logo} alt="Logo" className="h-28 mb-10 animate-pulse" />

        <div className="bg-black/30 rounded-2xl p-10 shadow-2xl text-center max-w-2xl w-full mx-4">
          {/* Engrenagem girando */}
          <Settings className="w-16 h-16 mx-auto mb-6 text-green-300 animate-spin" />

          <h2 className="text-2xl font-bold mb-1">Aguarde...</h2>
          <p className="text-sm text-green-300 mb-4">
            Atualizando para a versão{" "}
            <strong>{dadosVersao?.versao || "1.0.x"}</strong>
          </p>

          <p className="text-base text-white/80 mb-6">
            {dadosVersao?.mensagem || "Atualizando o sistema..."}
          </p>

          {/* Barra de progresso */}
          <div className="w-full bg-white/30 rounded-full h-4 overflow-hidden relative">
            <div
              className="h-4 rounded-full transition-all duration-500 animate-progress-gradient"
              style={{
                width: `${progresso}%`,
                background:
                  "linear-gradient(270deg, #00ff88, #00c6ff, #0072ff, #00c6ff, #00ff88)",
                backgroundSize: "300% 300%",
              }}
            />
          </div>
          <p className="mt-3 text-sm text-white/70">
            {Math.round(progresso)}%
          </p>

          {/* Frase explicativa */}
          <p className="mt-6 text-white/90 text-sm italic">
            ⚙️ Nosso sistema entrou em modo de atualização.  
            Em instantes você poderá utilizá-lo novamente.
          </p>
        </div>

        <p className="absolute bottom-6 text-white/80 text-sm">
          Cotrol Service - Atualização do Sistema
        </p>

        {/* Estilo para animar gradiente */}
        <style>{`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-progress-gradient {
            animation: gradientMove 2s linear infinite;
          }
        `}</style>
      </div>
    );
  }

  if (step === "sucesso") {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-black/90 text-green-400 text-2xl font-bold animate-bounce">
        Sistema atualizado com sucesso!
      </div>
    );
  }

  if (step === "novidades") {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50 px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-5xl w-full relative space-y-6">
          <button
            className="absolute top-3 right-4 text-red-600 text-2xl font-bold hover:scale-110 transition"
            onClick={marcarAtualizacaoComoVista}
            title="Fechar"
          >
            ✕
          </button>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              🆕 Novidades da versão {dadosVersao?.versao}
            </h2>

            {dadosVersao?.observacao && (
              <p className="text-sm text-gray-700 border-l-4 border-yellow-400 pl-4 bg-yellow-50 py-1 rounded-md">
                {dadosVersao.observacao}
              </p>
            )}
          </div>

          <ul className="space-y-3 text-gray-800 text-base max-h-[50vh] overflow-y-auto pr-2">
            {(dadosVersao?.novidades || []).map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-xl mt-1">{item.icone || "📝"}</span>
                <div>
                  <strong>{item.titulo || "Atualização"}:</strong>{" "}
                  <span>{item.descricao || "Sem descrição."}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex justify-end">
            <button
              onClick={marcarAtualizacaoComoVista}
              className="mt-2 px-6 py-2 bg-blue-600 text-white font-medium rounded-md shadow hover:bg-blue-700 transition"
            >
              Ir para o sistema
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
